import React, {useCallback, useEffect, useMemo, useState} from "react";
import SimpleMoreDetailsDialog from "../../../../../../components/dialogs/simple-more-details-dialog";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {InventoryAdjustmentCommittedViewDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields} from "../../../../../../../packages/data-grid";
import {ArrowBack, ChevronRightRounded} from "@mui/icons-material";
import classnames from "classnames";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../../../containers/data-grid";
import {DefaultInitialPaginationInfo} from "../../../../../../../core/constants/enums";
import Chips, {ChipsContentType, ChipsTypes} from "../../../../../../components/base/chips/chips";
import {Col} from "reactstrap";
import SummaryInfoCard from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import Api from "../../../../../../../core/services/api/api";
import {formatMoney, numComparator, stringComparator} from "../../../../../../../core/services/utils/utils";
import IconButton from "../../../../../../components/base/button/icon_button";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import {routes} from "../../../../../../../core/models/static/routes/app-routes";
import ToastService from "../../../../../../../core/services/toast";
import {useHistory, useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";

const _initialPaginationInfo = {
    ...DefaultInitialPaginationInfo,
    currentPage: 1,
    pageSize: 10,
}

const dataGridSaveConfig = {
    name: 'InventoryAdjustmentSingleViewDataGrid',
    version: 2,
}

const adjustmentBasicInfoBodyIds = {
    code: 'code',
    type: 'type',
    submittedDate: 'submittedDate',
}

const adjustmentDetailInfoBodyIds = {
    productCount: 'productCode',
    totalCount: 'totalCount',
    totalCost: 'totalCost',
    operator: 'operator'
}

const triggerCurrentPage = -1;

const InventoryAdjustmentSingleView = () => {
    const params = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [info, setInfo] = useState(null);
    const [items, setItems] = useState([]);
    const [fullNoteDialogOpen, setFullNoteDialogOpen] = useState(false);
    const [fullNote, setFullNote] = useState(null);
    const [visibleItems, setVisibleItems] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('inventory-adjustment-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportInventoryAdjustments = operatorAccessLevels.packages.bizInventory.exportInventoryAdjustments;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.inventoryAdjustmentInformation(params.id));
        configureBehavioralTool((tags) => tags.inventoryAdjustmentInformation(params.id));
    }, [configureBehavioralTool, configureNotebookTags, params.id]);

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: DataGridExoticColumnFields.detailedPanelToggler,
            },
            {
                title: "Part Number",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.partNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Category",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Quantity",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.quantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 90,
            },
            {
                title: "Quantity Change",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.quantityChange,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 140,
            },
            {
                title: "New Quantity",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.newQuantity,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 140,
            },
            {
                title: "Cost",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.cost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 80,
            },
            {
                title: "Extended Cost",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.extendedCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 110,
                sortable: false,
            },
            {
                title: "Average Cost",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.averageCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 100,
            },
            {
                title: "Warehouse",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.warehouse,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 100,
            },
            {
                title: "Reason Code",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.reasonCode,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 100,
            },
            {
                title: "Reason",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.reason,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 200,
                sortable: false,
            },
            {
                title: "Note",
                name: InventoryAdjustmentCommittedViewDataGridCellKeys.note,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 400,
                sortable: false,
            },
            {
                name: DataGridExoticColumnFields.detailedPanel,
            }
        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo, [dataGridPageSize]);

    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
    const [orderBy, setOrderBy] = useState(dataGridSortBy);

    /**@type {function(a, b): number}*/
    const orderByMethod = useMemo(() => {
        switch (orderBy?.field) {
            case InventoryAdjustmentCommittedViewDataGridCellKeys.partNumber:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.partNumber ?? '', b?.partNumber ?? '')
                    : stringComparator(b?.partNumber ?? '', a?.partNumber ?? '')
            case InventoryAdjustmentCommittedViewDataGridCellKeys.category:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.category ?? '', b?.category ?? '')
                    : stringComparator(b?.category ?? '', a?.category ?? '')
            case InventoryAdjustmentCommittedViewDataGridCellKeys.quantity:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.quantity, b?.quantity)
                    : numComparator(b?.quantity, a?.quantity)
            case InventoryAdjustmentCommittedViewDataGridCellKeys.quantityChange:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.quantityChange, b?.quantityChange)
                    : numComparator(b?.quantityChange, a?.quantityChange)
            case InventoryAdjustmentCommittedViewDataGridCellKeys.newQuantity:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.newQuantity, b?.newQuantity)
                    : numComparator(b?.newQuantity, a?.newQuantity)
            case InventoryAdjustmentCommittedViewDataGridCellKeys.cost:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.cost, b?.cost)
                    : numComparator(b?.cost, a?.cost)
            case InventoryAdjustmentCommittedViewDataGridCellKeys.extendedCost:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.extendedCost, b?.extendedCost)
                    : numComparator(b?.extendedCost, a?.extendedCost)
            case InventoryAdjustmentCommittedViewDataGridCellKeys.averageCost:
                return (a, b) => orderBy.descending
                    ? numComparator(a?.averageCost, b?.averageCost)
                    : numComparator(b?.averageCost, a?.averageCost)
            case InventoryAdjustmentCommittedViewDataGridCellKeys.warehouse:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.warehouse ?? '', b?.warehouse ?? '')
                    : stringComparator(b?.warehouse ?? '', a?.warehouse ?? '')
            case InventoryAdjustmentCommittedViewDataGridCellKeys.reasonCode:
                return (a, b) => orderBy.descending
                    ? stringComparator(a?.reasonCode ?? '', b?.reasonCode ?? '')
                    : stringComparator(b?.reasonCode ?? '', a?.reasonCode ?? '')
            default:
                return () => 0
        }
    }, [orderBy]);

    /**
     * With each change in the [data] and [paginationInfo] values of the state:
     * - selects a slice of the data to be set as the [visibleItems] state value.
     */
    useEffect(() => {
        if (!items?.length) {
            setPaginationInfo(initialPaginationInfo);
            return setVisibleItems([]);
        }
        if (paginationInfo.currentPage === triggerCurrentPage) {
            setPaginationInfo(initialPaginationInfo);
            return;
        }
        let results = Array.from(items);
        const start = Math.min(Math.max((paginationInfo.currentPage - 1) * paginationInfo.pageSize, 0), results.length);
        const end = Math.min((paginationInfo.currentPage) * paginationInfo.pageSize, results.length);
        const chunk = Array.from(results).sort(orderByMethod).slice(start, end)
        setPaginationInfo(prevState => ({...prevState, length: results.length}));
        setVisibleItems(chunk);
    }, [items, paginationInfo.currentPage, paginationInfo.pageSize, orderByMethod, initialPaginationInfo])

    /**
     * Fetches the adjustment information from the server
     * @type {function(): Promise<void>}
     */
    const getAdjustmentInformation = useCallback(async () => {
        setLoading(true);
        const response = await Api.getInventoryAdjustment(Number(params.id));
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag && !!response?.data) {
            setInfo({
                code: response.data.code,
                type: response.data.isAdd ? 'Add' : "Remove",
                submittedDate: response.data.submittedDatetime,
                productCount: response.data.productCount,
                totalCount: response.data.totalProductCount,
                totalCost: response.data.totalCost,
                operatorName: response.data.submittedBy,
                description: response.data.description,
            })
            setItems(response.data.adjustmentItems?.map(item => ({
                id: item.id,
                partNumber: item.partNumber,
                category: item.partCategory,
                quantity: item.oldQty,
                quantityChange: (item.qty * (response?.data?.isAdd ? 1 : -1)),
                newQuantity: item.oldQty + (item.qty * (response?.data?.isAdd ? 1 : -1)),
                cost: item.cost,
                extendedCost: item.serials?.reduce((p, e) => p + e.cost, 0) ?? (item.cost * (item.qty * (response?.data?.isAdd ? 1 : -1))),
                averageCost: item.avgCost,
                warehouse: item.warehouseCode,
                reasonCode: item.reason?.code,
                reason: item.reason?.title,
                note: item.description,
                serialNumbers: item.serials ?? [],
                isSerialized: item.serials !== null,
            })) ?? [])
        } else {
            ToastService.error({
                title: 'Not Found',
                message: 'Failed to get the adjustment information',
            });
            history.replace(routes.inventory.inventoryAdjustment.list);
        }
    }, [isMounted, params.id, history])


    /**
     * With initial render of the view:
     * Calls the function for getting the information of an adjustment in the system
     */
    useEffect(() => {
        void getAdjustmentInformation();
    }, [getAdjustmentInformation])

    /**
     * Exports the requests using the same search filters to csv
     */
    const exportToCsv = async () => {
        const exportTitle = `Inventory Adjustment #${info?.code ?? ''} (${info?.type}) by ${info?.operatorName} `;
        return {
            title: exportTitle,
            body: items?.map((item, index) => ({
                "": index + 1,
                "Part Number": item.partNumber ?? '--',
                "Category": item.category ?? '--',
                "Quantity": item.quantity ?? '--',
                "Quantity Change": item.quantityChange ?? '--',
                "New Quantity": item.newQuantity ?? '--',
                "Cost": item.cost ? formatMoney(item.cost) : '--',
                "Extended Cost": item.extendedCost ? formatMoney(item.extendedCost) : '--',
                "Average Cost": item.averageCost ? formatMoney(item.averageCost) : '--',
                "Warehouse Code": item.warehouse ?? '--',
                "Reason Code": item.reasonCode ?? '--',
                "Reason": item.reason ?? '--',
                "note": item.note ?? '--',
                "Serial Numbers": item.serialNumbers?.reduce((a, s, index, array) => a.concat(s.number).concat(index !== array.length - 1 ? ' | ' : ''), '') ?? '--',
            })) ?? [],
        }
    }

    /**
     * Opens a dialog for showing the note full details
     * @param note
     */
    const viewFullNote = (note) => {
        setFullNote(note);
        setFullNoteDialogOpen(true);
    }

    /**
     * Width of the serial numbers wrapper in the expandable section of data grid
     * @type {string}
     */
    const expandableSectionWidth = useMemo(() => {
        const dataGridHeader = document.getElementsByClassName('data-grid-toolbar')?.[0];
        return `${dataGridHeader?.offsetWidth}px` ?? '0';
    }, [loading, items])

    /**
     * Sorts the entries of the credits in place.
     * @param {DataGridSortBy} tableOrderBy
     */
    const onSort = useCallback((tableOrderBy) => {
        setOrderBy(tableOrderBy);
        setPaginationInfo(prevState => ({...prevState, currentPage: triggerCurrentPage}));
    }, [])

    /**
     * Memo version of data grid Rows
     * @type {DataGridRow[]}
     */
    const rows = useMemo(() => {
        return visibleItems?.map((record) => ({
            key: record.id,
            data: record,
            cells: {
                [DataGridExoticColumnFields.detailedPanelToggler]: null,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.partNumber]:
                    <ProductInformationPopover partNo={record?.partNumber}>
                        {record?.partNumber ?? '--'}
                    </ProductInformationPopover>,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.category]: record?.category,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.quantity]: record?.quantity,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.quantityChange]: record?.quantityChange,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.newQuantity]: record?.newQuantity,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.cost]: record?.cost,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.extendedCost]: record?.extendedCost,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.averageCost]: record?.averageCost,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.warehouse]: record?.warehouse,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.reasonCode]: record?.reasonCode,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.reason]: record?.reason,
                [InventoryAdjustmentCommittedViewDataGridCellKeys.note]: record?.note?.length > 30
                    ? <div className={'note-container'}>
                        <p {...dataGridCellTextProps} className={'note'}>
                            {`${record?.note.substring(0, 25)}...`}
                        </p>
                        <div
                            className={classnames('part-number-text-button')}
                            onClick={() => viewFullNote(record?.note)}
                        >
                            <p className={'mb-1'}>More</p>
                            <ChevronRightRounded className='icon small' color={"primary"}/>
                        </div>
                    </div>
                    :
                    <p {...dataGridCellTextProps}>{record?.note}</p>,
                [DataGridExoticColumnFields.detailedPanel]: !record.isSerialized
                    ? null
                    : <div
                        className={'serial-numbers-wrapper'}
                        style={{
                            width: expandableSectionWidth,
                        }}
                    >
                        {
                            record?.serialNumbers?.map(e =>
                                <span  {...dataGridCellTextProps} key={e.number} className={'mr-2 mb-2'}>
                                    <Chips
                                        contentType={ChipsContentType.text}
                                        type={ChipsTypes.none}
                                        content={`${e.number} (${formatMoney(e.cost)})`}
                                    />
                                </span>
                            )
                        }
                    </div>,
            }
        }))
    }, [visibleItems, expandableSectionWidth])

    const adjustmentBasicInfo = useMemo(() => ({
        header: {
            title: '',
            value: '',
            loadingValue: "Loading...",
        },
        body: [
            {
                id: adjustmentBasicInfoBodyIds.code,
                title: 'Code',
                value: info?.code,
                copyable: true,
            },
            {
                id: adjustmentBasicInfoBodyIds.type,
                title: 'Type',
                value: info?.type,
            },
            {
                id: adjustmentBasicInfoBodyIds.submittedDate,
                title: 'Submitted Date',
                value: DateUtils.serverToLocal(info?.submittedDate).format(DateUtils.dateTimeFormat),
            },
        ],
    }), [
        info?.code,
        info?.type,
        info?.submittedDate,
    ])

    const adjustmentDetailInfo = useMemo(() => ({
        body: [
            {
                id: adjustmentDetailInfoBodyIds.productCount,
                title: 'Product Count',
                value: `${info?.productCount ?? 0}`,
            },
            {
                id: adjustmentDetailInfoBodyIds.totalCount,
                title: 'Total Product Count',
                value: `${info?.totalCount ?? 0}`,
            },
            {
                id: adjustmentDetailInfoBodyIds.totalCost,
                title: 'Total Cost',
                value: formatMoney(info?.totalCost),
            },
            {
                id: adjustmentDetailInfoBodyIds.operator,
                title: 'Operator',
                value: info?.operatorName,
            },
        ],
    }), [
        info?.productCount,
        info?.totalCount,
        info?.totalCost,
        info?.operatorName,
    ])

    return (
        <>
            <div className={'inventory-adjustment-committed-view'}>
                <div className={'d-flex flex-row align-items-center fast-connection-view w-100 px-2 mt-3 mb-4'}>
                    <IconButton
                        onPressed={() => history.goBack()}
                        color={'paper.secondary'}
                    >
                        <ArrowBack className='icon normal'/>
                    </IconButton>
                    <p className={'view-title ml-2'}>
                        Adjustment Committed View
                    </p>
                    <div/>
                </div>
                <div className='d-flex flex-wrap w-100 justify-content-center'>
                    <Col xs={12} md={6} xl={!!info?.description ? 4 : 6} className={'mb-3'}>
                        <SummaryInfoCard
                            body={adjustmentBasicInfo.body}
                            loading={loading}
                            loadingCount={adjustmentBasicInfo.body.length}
                        />
                    </Col>
                    <Col xs={12} md={6} xl={!!info?.description ? 4 : 6} className={'mb-3'}>
                        <SummaryInfoCard
                            body={adjustmentDetailInfo.body}
                            loading={loading}
                            loadingCount={adjustmentDetailInfo.body.length}
                        />
                    </Col>
                    {
                        !!info?.description &&
                        <Col xs={12} md={6} xl={4} className={'mb-3'}>
                            <div className={'summary-info-card'}>
                                <p className={'description-title'}>Description</p>
                                <p className={'description-body'}>{info?.description}</p>
                            </div>
                        </Col>
                    }
                </div>
                <div className={'mb-4'}/>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={rows}
                    state={{
                        sortBy: orderBy
                            ? {field: orderBy.propertyName, descending: orderBy.isDescending}
                            : null,
                        pagination: paginationInfo,
                        loading: {state: loading},
                        density: dataGridDensity,
                    }}
                    hideFooterActions
                    getExportInfo={canExportInventoryAdjustments ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => onSort(current)}
                    onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                    onPageSizeChanged={(_, current) => setPaginationInfo(current)}
                />
            </div>
            <SimpleMoreDetailsDialog
                open={fullNoteDialogOpen}
                setOpen={setFullNoteDialogOpen}
                title={'Note'}
                details={fullNote}
            />
        </>
    );
}

export default InventoryAdjustmentSingleView;
